import React, {
    useEffect,
    useState,
    useRef,
    ChangeEvent,
    useContext,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import history from '../Shared/history';
import { useLocation } from 'react-router-dom';

// components and helpers
import { userActions } from './UserActions';
import { showNotification } from '../Notifications/NotificationActions';
import utils from '../../utils/utils';
import { AppContext } from '../../containers/AppProviderWrapper';
import UpgradeContactDialog from './UpgradeContactDialog';

// mui
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import { Badge } from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import InputBase from '@mui/material/InputBase';
import { makeStyles } from '@mui/styles';
import Avatar from '@mui/material/Avatar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CancelIcon from '@mui/icons-material/Cancel';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Paper, useTheme } from '@mui/material';
import { useMediaQuery } from '@mui/material';

const useStyles = makeStyles({
    root: {
        '& .MuiInput-underline:before': {
            borderBottom: 'none', // Remove the underline
        },
        '& .MuiInput-underline:after': {
            borderBottom: 'none', // Remove the underline
        },
    },
});

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}
function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}
function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const Profile: React.FC = () => {
    // Basic setup
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down(900));
    const dispatch = useDispatch();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const classes = useStyles();
    const { currentPath } = useContext(AppContext);
    const location = useLocation();

    // State, useState setup
    const [upgradeDialogOpen, setUpgradeDialogOpen] = useState<boolean>(false);
    const [selectedAvatar, setSelectedAvatar] = useState<string>('');
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [value, setValue] = useState<number>(0);
    const [error, setError] = useState<boolean>(false);
    const [, setPhoneNumber] = useState<string>('');
    const [accountInfo, setAccountInfo] = useState({
        firstName: '',
        lastName: '',
        phoneNumber: '',
        company: '',
        jobTitle: '',
        userId: '',
        id: '',
        role: '',
    });
    const [accountPictureInfo, setAccountPictureInfo] = useState({
        accountPicture: useSelector((state: any) => state.user.avatar),
    });
    const [originalAccountInfo, setOriginalAccountInfo] = useState({
        ...accountInfo,
    });
    // useSelector setup
    const currentUser = useSelector((state: any) => state.user.currentUser);
    const currentUserPicture = useSelector(
        (state: any) => state.user.currentUserPicture
    );
    const currentUserLoading = useSelector(
        (state: any) => state.user.currentUserLoading
    );

    // fields stuff, this had to go after error setup

    const fields = [
        { label: 'First Name', name: 'firstName', placeholder: 'John' },
        { label: 'Last Name', name: 'lastName', placeholder: 'Doe' },
        {
            label: 'Phone Number',
            name: 'phoneNumber',
            placeholder: '123-456-7890',
            type: 'tel',
            inputProps: { pattern: '[0-9]*', maxLength: 12 },
            adornment: isEditing ? '+1' : '',
            valueFormatter: (value) => {
                if (value && value.replace('---', '').length > 0) {
                    return `+1-${value}`;
                } else {
                    return value;
                }
            },

            error: error,
            helperText: error ? 'Invalid phone number' : null,
        },
        { label: 'Organization', name: 'company', placeholder: 'ABC Inc' },
        { label: 'Job Title', name: 'jobTitle', placeholder: 'Pathologist' },
    ];

    useEffect(() => {
        const userData = {
            ...accountPictureInfo,
            avatar: selectedAvatar,
        };

        if (!currentUserLoading && !accountInfo.userId) {
            setAccountInfo(currentUser);
            setAccountPictureInfo(userData);
        }
    }, [
        currentUser,
        currentUserLoading,
        accountPictureInfo,
        selectedAvatar,
        accountInfo.phoneNumber,
        accountInfo.userId,
    ]);

    // Functions that do mysterious things

    // First off, handling the file

    const handleChooseFile = () => {
        fileInputRef.current?.click();
    };

    // Next up is the handle change functions

    useEffect(() => {
        if (location && location.search.includes('?id')) {
            const id = location.search.split('=')[1].split('&')[0];
            const index = id === 'billing' ? 1 : 0;
            setValue(index);
        } else {
            history.push(`${currentPath}?id=profile`);
        }
    }, [location, currentPath]);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        let location = 'profile';
        if (newValue === 1) {
            location = 'billing';
        }
        history.push(`${currentPath}?id=${location}`);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'phoneNumber') {
            let formattedInput = value.replace(/\D/g, ''); // remove non-numbers
            if (formattedInput.length > 3) {
                const part1 = formattedInput.substring(0, 3);
                const part2 = formattedInput.substring(3);
                formattedInput = `${part1}-${part2}`;
            }
            if (formattedInput.length > 7) {
                const part1 = formattedInput.substring(0, 7);
                const part2 = formattedInput.substring(7);
                formattedInput = `${part1}-${part2}`;
            }
            if (formattedInput.length <= 11 && formattedInput.length >= 1) {
                setError(true);
            }
            if (formattedInput.length >= 12 || formattedInput.length === 0) {
                setError(false);
            }
            setAccountInfo((prevInfo) => ({
                ...prevInfo,
                [name]: formattedInput,
            }));
            setPhoneNumber(formattedInput);
        } else {
            setAccountInfo((prevInfo) => ({
                ...prevInfo,
                [name]: value,
            }));
        }
    };

    const handleAvatarChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files && event.target.files[0];
        if (!file) {
            // No file selected, exit early
            return;
        }

        if (accountPictureInfo.accountPicture) {
            // An image is already selected, handle the error condition here
            return;
        }

        // @ts-ignore
        setSelectedAvatar(file);

        dispatch(userActions?.updatePicture(file));
    };

    // Last but not least is the handle button click functions

    const handleEditClick = () => {
        setOriginalAccountInfo({ ...accountInfo });
        setIsEditing(true);
    };

    const handleCancelClick = () => {
        setAccountInfo({ ...originalAccountInfo });
        setIsEditing(false);
        setError(false);
    };

    const handleLockClick = () => {
        if (!error) {
            setIsEditing(false);
            dispatch(userActions.updateUser(accountInfo));
        } else {
            dispatch(
                showNotification(
                    'The information you entered was invalid. Please try again',
                    'error'
                )
            );
        }
    };

    // The actual important stuff that does the thing

    const daysRemaining =
        currentUser &&
        currentUser.userConfiguration &&
        currentUser.userConfiguration.expirationDate &&
        moment(currentUser.userConfiguration.expirationDate).diff(
            moment(),
            'days'
        );

    return (
        <Grid container>
            <Grid item xs={12} justifyContent="center">
                <Typography variant="h5" align="left">
                    My Account
                </Typography>
            </Grid>
            <Grid item xs={12} justifyContent="center">
                <Box justifyContent="center">
                    <Tabs
                        value={value}
                        onChange={handleChange}
                        aria-label="basic tabs example"
                        centered
                    >
                        <Tab label="My Account" {...a11yProps(0)} />
                        <Tab label="Billing" {...a11yProps(1)} />
                    </Tabs>
                </Box>
                <TabPanel value={value} index={0}>
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            style={{ padding: 10, borderRadius: 15 }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '20px',
                                    maxWidth: '65%',
                                    margin: '0 auto',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                }}
                            >
                                <Badge
                                    overlap="circular"
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    badgeContent={
                                        <IconButton
                                            onClick={handleChooseFile} // Handle click to open file selector
                                            size="small"
                                            style={{
                                                backgroundColor:
                                                    'rgba(0, 0, 0, 0.6)',
                                                color: 'white',
                                            }}
                                        >
                                            <PhotoCamera />
                                        </IconButton>
                                    }
                                >
                                    <Avatar
                                        src={currentUserPicture}
                                        sx={{ width: 100, height: 100 }}
                                    />
                                </Badge>

                                <InputBase
                                    inputRef={fileInputRef}
                                    type="file"
                                    style={{ display: 'none' }}
                                    inputProps={{ accept: 'image/*' }}
                                    onChange={handleAvatarChange}
                                />
                            </Box>
                            <Box
                                sx={{
                                    padding: '10px',
                                    maxWidth: '65%',
                                    margin: '0 auto',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        align="left"
                                        fontWeight={'bold'}
                                    >
                                        My Details
                                    </Typography>
                                    {isEditing ? (
                                        <>
                                            <IconButton
                                                onClick={handleCancelClick}
                                            >
                                                <CancelIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={handleLockClick}
                                            >
                                                <SaveIcon />
                                            </IconButton>
                                        </>
                                    ) : (
                                        <IconButton onClick={handleEditClick}>
                                            <EditIcon />
                                        </IconButton>
                                    )}
                                </div>
                                <hr
                                    style={{
                                        borderTop: '2px solid',
                                        borderColor: theme.palette.primary.main,
                                        width: '100%',
                                        marginTop: '10px',
                                        marginBottom: '20px',
                                    }}
                                />
                                <>
                                    {fields.map((field) => (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginBottom: '10px',
                                            }}
                                        >
                                            <TextField
                                                id="outlined-basic"
                                                label={field.label}
                                                name={field.name}
                                                variant={
                                                    isEditing
                                                        ? 'outlined'
                                                        : 'standard'
                                                }
                                                type={field.type}
                                                style={{ width: '100%' }}
                                                placeholder={field.placeholder}
                                                value={
                                                    isEditing
                                                        ? accountInfo[
                                                              field.name
                                                          ] || ''
                                                        : field.valueFormatter
                                                        ? field.valueFormatter(
                                                              accountInfo[
                                                                  field.name
                                                              ] || '---'
                                                          )
                                                        : accountInfo[
                                                              field.name
                                                          ] || '---'
                                                }
                                                onChange={handleInputChange}
                                                size={'small'}
                                                disabled={!isEditing}
                                                className={
                                                    !isEditing
                                                        ? classes.root
                                                        : ''
                                                }
                                                inputProps={field.inputProps}
                                                InputProps={
                                                    field.adornment
                                                        ? {
                                                              startAdornment: (
                                                                  <InputAdornment position="start">
                                                                      {
                                                                          field.adornment
                                                                      }
                                                                  </InputAdornment>
                                                              ),
                                                          }
                                                        : {}
                                                }
                                                error={field.error}
                                                helperText={field.helperText}
                                                sx={{
                                                    '& .MuiInputBase-input.Mui-disabled':
                                                        {
                                                            WebkitTextFillColor:
                                                                theme.palette
                                                                    .primary
                                                                    .main,
                                                        },
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </>
                                <Typography
                                    variant="h6"
                                    align="left"
                                    fontWeight={'bold'}
                                >
                                    Account Details
                                </Typography>
                                <hr
                                    style={{
                                        borderTop: '2px solid',
                                        borderColor: theme.palette.primary.main,
                                        width: '100%',
                                        marginTop: '10px',
                                        marginBottom: '20px',
                                    }}
                                />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '10px',
                                    }}
                                >
                                    <TextField
                                        id="outlined-basic"
                                        label="Account ID"
                                        name={'userId'}
                                        variant="standard"
                                        style={{ width: '100%' }}
                                        placeholder="johndoe@gmail.com"
                                        value={accountInfo.userId}
                                        onChange={handleInputChange}
                                        size={'small'}
                                        disabled={true}
                                        className={classes.root}
                                        sx={{
                                            '& .MuiInputBase-input.Mui-disabled':
                                                {
                                                    WebkitTextFillColor:
                                                        theme.palette.primary
                                                            .main,
                                                },
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                </TabPanel>
                <TabPanel value={value} index={1}>
                    <Grid
                        container
                        justifyContent={'center'}
                        alignItems="center"
                        style={{
                            position: matches ? 'fixed' : 'static',
                            top: 0,
                            left: 0,
                            width: matches ? '80%' : '100%',
                            zIndex: 999,
                            padding: matches ? '0% 0% 0% 25%' : '0px',
                        }}
                    >
                        <Grid item xs={12}>
                            <Paper
                                elevation={0}
                                style={{ padding: 10, borderRadius: 15 }}
                            >
                                <Box
                                    sx={{
                                        padding: '10px',
                                        maxWidth: '65%',
                                        margin: '0 auto',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            align="left"
                                            fontWeight={'bold'}
                                        >
                                            My Subscription
                                        </Typography>
                                    </div>
                                    <hr
                                        style={{
                                            borderTop: '2px solid',
                                            borderColor:
                                                theme.palette.primary.main,
                                            width: '100%',
                                            marginTop: '10px',
                                            marginBottom: '20px',
                                        }}
                                    />
                                    {currentUser &&
                                    currentUser.userConfiguration &&
                                    currentUser.userConfiguration.beta ? (
                                        <Grid
                                            container
                                            spacing={4}
                                            justifyContent={'flex-end'}
                                        >
                                            <Grid item xs={4}>
                                                <Typography
                                                    variant={'body1'}
                                                    color={'primary'}
                                                    sx={{ fontWeight: 'bold' }}
                                                >
                                                    Subscription Type
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                    Free Trial
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography
                                                    variant={'body1'}
                                                    color={'primary'}
                                                    sx={{ fontWeight: 'bold' }}
                                                >
                                                    Expiration Date
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography>
                                                    {currentUser &&
                                                        currentUser.userConfiguration &&
                                                        currentUser
                                                            .userConfiguration
                                                            .expirationDate &&
                                                        utils.formatDate(
                                                            currentUser
                                                                .userConfiguration
                                                                .expirationDate
                                                        )}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Typography
                                                    variant={'body1'}
                                                    color={'primary'}
                                                    sx={{ fontWeight: 'bold' }}
                                                >
                                                    Time Remaining
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography
                                                    color={
                                                        daysRemaining < 7
                                                            ? theme.palette
                                                                  .mode ===
                                                              'dark'
                                                                ? '#FF4242'
                                                                : '#B30000'
                                                            : 'inherit'
                                                    }
                                                >
                                                    {daysRemaining > 0
                                                        ? `${daysRemaining} Days`
                                                        : 'Expired'}
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Button
                                                    variant={'contained'}
                                                    color={'primary'}
                                                    onClick={() =>
                                                        setUpgradeDialogOpen(
                                                            true
                                                        )
                                                    }
                                                >
                                                    Upgrade
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    ) : (
                                        <Grid
                                            container
                                            justifyContent={'center'}
                                        >
                                            <Grid item>
                                                <Typography
                                                    variant="h6"
                                                    style={{
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    Coming Soon
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    )}
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </TabPanel>
            </Grid>
            <UpgradeContactDialog
                open={upgradeDialogOpen}
                handleClose={() => setUpgradeDialogOpen(false)}
            />
        </Grid>
    );
};

export default Profile;
